/**
 * Catálogo de intenções de check-in e o mapeamento de quais intenções fazem
 * sentido para cada categoria de estabelecimento (`src/lib/venueCategories.ts`).
 *
 * O Zappy atende estabelecimentos muito diferentes entre si (bar, academia,
 * salão de beleza, congresso, hotel...), então a lista de intenções mostrada
 * no check-in (`VenueQrCheckin.tsx`) é filtrada por categoria a partir desta
 * lista mestra — em vez de exibir sempre as mesmas opções para qualquer local.
 *
 * `group` e `music` continuam exigindo um texto complementar (turma / música),
 * validado em `validateVenueCheckin.ts` e no servidor (RPC
 * `validate_venue_location_checkin_for_user`).
 */
import {
  type LucideIcon,
  UserPlus,
  MessageCircle,
  UsersRound,
  Music,
  Briefcase,
  Sparkles,
  Dumbbell,
  BookOpen,
  Mic2,
  PartyPopper,
  Utensils,
  Scissors,
  Dices,
  Plane,
  MapPin,
} from "lucide-react";
import type { VenueCategory } from "@/lib/venueCategories";

export const CHECKIN_INTENTION_VALUES = [
  "new_people",
  "conversation",
  "group",
  "music",
  "networking",
  "chill",
  "workout",
  "learn",
  "watch_show",
  "family_fun",
  "try_food",
  "pamper",
  "bet_fun",
  "travel_meet",
  "just_passing",
  "flirt",
] as const;

export type CheckinIntention = (typeof CHECKIN_INTENTION_VALUES)[number];

export type CheckinIntentionDefinition = {
  value: CheckinIntention;
  label: string;
  icon: LucideIcon;
  /** Exige um texto complementar (campo "Turma" ou "Música") antes de confirmar. */
  requiresLabel?: "group" | "music";
};

export const CHECKIN_INTENTION_CATALOG: readonly CheckinIntentionDefinition[] = [
  { value: "new_people", label: "Conhecer gente nova", icon: UserPlus },
  { value: "conversation", label: "Conversar", icon: MessageCircle },
  { value: "group", label: "Minha turma", icon: UsersRound, requiresLabel: "group" },
  { value: "music", label: "Música", icon: Music, requiresLabel: "music" },
  { value: "networking", label: "Networking", icon: Briefcase },
  { value: "chill", label: "Relaxar", icon: Sparkles },
  { value: "workout", label: "Treinar", icon: Dumbbell },
  { value: "learn", label: "Aprender", icon: BookOpen },
  { value: "watch_show", label: "Aproveitar atração", icon: Mic2 },
  { value: "family_fun", label: "Diversão em família", icon: PartyPopper },
  { value: "try_food", label: "Gastronomia", icon: Utensils },
  { value: "pamper", label: "Cuidar da aparência", icon: Scissors },
  { value: "bet_fun", label: "Jogar", icon: Dices },
  { value: "travel_meet", label: "Conhecer viajantes", icon: Plane },
  { value: "just_passing", label: "Só de passagem", icon: MapPin },
  { value: "flirt", label: "Flertar", icon: MessageCircle },
];

export const CHECKIN_INTENTION_LABELS: Record<CheckinIntention, string> = CHECKIN_INTENTION_CATALOG.reduce(
  (labels, item) => ({ ...labels, [item.value]: item.label }),
  {} as Record<CheckinIntention, string>,
);

const CHECKIN_INTENTION_BY_VALUE: Record<CheckinIntention, CheckinIntentionDefinition> =
  CHECKIN_INTENTION_CATALOG.reduce(
    (byValue, item) => ({ ...byValue, [item.value]: item }),
    {} as Record<CheckinIntention, CheckinIntentionDefinition>,
  );

/** Intenções coerentes por categoria de estabelecimento, da mais para a menos provável. */
export const VENUE_CATEGORY_INTENTIONS: Record<VenueCategory, CheckinIntention[]> = {
  bar: ["new_people", "conversation", "music", "networking"],
  pub: ["music", "new_people", "conversation"],
  restaurant: ["try_food", "conversation", "networking", "new_people"],
  cafeteria: ["conversation", "networking", "try_food", "new_people"],
  club: ["music", "new_people", "conversation"],
  lounge: ["chill", "conversation", "networking"],
  rooftop: ["chill", "conversation", "new_people"],
  beach_club: ["chill", "new_people"],
  espaco_shows: ["watch_show", "music", "new_people", "conversation"],
  espaco_eventos: ["networking", "learn", "watch_show", "new_people", "conversation"],
  espaco_festas: ["group", "watch_show", "new_people"],
  coworking: ["networking", "learn", "conversation", "new_people"],
  hotel: ["travel_meet", "chill", "new_people", "conversation"],
  hostel: ["travel_meet", "new_people", "group", "conversation"],
  resort: ["chill", "family_fun", "travel_meet", "new_people"],
  festival: ["watch_show", "new_people", "group"],
  feira: ["learn", "networking", "new_people"],
  congresso: ["networking", "learn", "new_people", "conversation"],
  auditorio: ["learn", "watch_show", "conversation"],
  teatro: ["watch_show", "conversation", "new_people"],
  circo: ["family_fun", "watch_show", "new_people"],
  mercado_gastronomico: ["try_food", "conversation", "new_people", "networking"],
  academia: ["workout", "group", "new_people", "conversation"],
  clube_lazer: ["chill", "family_fun", "new_people", "conversation"],
  clube_esportes: ["workout", "new_people", "group", "conversation"],
  estadio: ["watch_show", "group", "new_people", "conversation"],
  ginasio: ["workout", "watch_show", "group", "new_people"],
  piscina: ["chill", "family_fun", "workout", "new_people"],
  parque_diversao: ["family_fun", "watch_show", "new_people", "group"],
  cassino: ["bet_fun", "new_people", "conversation"],
  barbearia: ["pamper", "conversation", "new_people"],
  salao_beleza: ["pamper", "chill", "conversation", "new_people"],
  espaco_convivencia: ["conversation", "new_people", "group", "chill"],
  other: ["just_passing", "new_people", "conversation"],
};

/** Escolhas pessoais disponíveis independentemente da atividade do estabelecimento. */
const GENERAL_INTENTIONS: readonly CheckinIntention[] = [
  "new_people", "conversation", "group", "chill", "flirt",
];

/** Usado quando a categoria do local é desconhecida ou é um texto livre fora da lista. */
const DEFAULT_INTENTIONS: CheckinIntention[] = VENUE_CATEGORY_INTENTIONS.other;

/** Intenções coerentes com a categoria do local, na ordem de relevância. */
export function getIntentionsForVenueCategory(
  category?: string | null,
): CheckinIntentionDefinition[] {
  const values =
    (category && VENUE_CATEGORY_INTENTIONS[category as VenueCategory]) || DEFAULT_INTENTIONS;
  return [...new Set<CheckinIntention>([...values, ...GENERAL_INTENTIONS])].map((value) => CHECKIN_INTENTION_BY_VALUE[value]).filter(Boolean);
}
